import { useState, useEffect } from 'react';
import { api } from '@/api';
import type { NewsItem } from '@/types/newsType';

interface UseNewsDataResult {
  newsItems: NewsItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch the latest news items from the API
 * Fetches 6 latest news items from /landing-news endpoint
 */
export const useNewsData = (): UseNewsDataResult => {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch 6 latest news items
      const response = await api.get<NewsItem[]>('landing-news', {
        params: {
          limit: 6,
          sort: '-createdAt', // Sort by creation date, newest first
        }
      });

      if (response.success && response.data) {
        setNewsItems(response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch news');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  return {
    newsItems,
    loading,
    error,
    refetch: fetchNews,
  };
};

export default useNewsData;
